import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '../lib/api'
import type { GroupPool, PoolMutationResponse, PoolResponse, WalletResponse } from '../lib/types'

// Contract error codes (build-contract §3.7) → copy PoolPanel shows inside its
// dialog. Unknown codes fall back to the server's own message.
const POOL_ERROR_COPY: Record<string, string> = {
  insufficient_wallet_funds: 'That’s more than your wallet balance — top up first.',
  exceeds_withdrawable: 'The pool can’t give that much back any more — it may have been spent.',
  amount_out_of_range: 'That amount is out of range.',
  feature_disabled: 'Group wallets aren’t enabled right now.',
}

function poolErrorMessage(e: unknown): string {
  if (e instanceof ApiError && e.code && POOL_ERROR_COPY[e.code]) return POOL_ERROR_COPY[e.code]
  return e instanceof Error ? e.message : 'Something went wrong — try again.'
}

/**
 * The group money pool plus the viewer's wallet balance (PoolPanel's client-side
 * chip-in cap). Pool mutations return BOTH objects (`{ pool, wallet }`, §3.7) so
 * a contribute/withdraw updates the panel and the cap atomically. Fetches only
 * while `enabled` (features.group_wallet) — a dark flag sends zero wallet
 * traffic. `contribute`/`withdraw` REJECT on failure so PoolPanel keeps its
 * dialog open, with friendly copy surfaced via `mutationError`.
 */
export function useGroupPool(groupId: string | undefined, enabled: boolean) {
  const [pool, setPool] = useState<GroupPool | null>(null)
  const [walletBalanceCents, setWalletBalanceCents] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [mutationError, setMutationError] = useState<string | null>(null)

  const refresh = useCallback(() => {
    if (!groupId || !enabled) return Promise.resolve()
    return Promise.all([
      api.get<PoolResponse>(`/groups/${groupId}/pool`),
      api.get<WalletResponse>('/wallet'),
    ])
      .then(([poolData, walletData]) => {
        setPool(poolData.pool)
        setWalletBalanceCents(walletData.wallet.balance_cents)
        setError(null)
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Could not load the group pool.')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [groupId, enabled])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const mutate = useCallback(
    async (path: 'contributions' | 'withdrawals', amountCents: number) => {
      if (!groupId) return
      setBusy(true)
      setMutationError(null)
      try {
        const data = await api.post<PoolMutationResponse>(`/groups/${groupId}/pool/${path}`, {
          amount_cents: amountCents,
        })
        // Mutations return BOTH objects — apply them together (§3.7).
        setPool(data.pool)
        setWalletBalanceCents(data.wallet.balance_cents)
      } catch (e) {
        setMutationError(poolErrorMessage(e))
        // A cap rejection means the balances the panel showed are stale
        // (someone spent the pool / the wallet changed) — re-sync so a retry
        // is honest.
        if (
          e instanceof ApiError &&
          (e.code === 'insufficient_wallet_funds' || e.code === 'exceeds_withdrawable')
        ) {
          void refresh()
        }
        throw e
      } finally {
        setBusy(false)
      }
    },
    [groupId, refresh],
  )

  const contribute = useCallback(
    (amountCents: number) => mutate('contributions', amountCents),
    [mutate],
  )
  const withdraw = useCallback(
    (amountCents: number) => mutate('withdrawals', amountCents),
    [mutate],
  )

  return {
    pool,
    walletBalanceCents,
    isLoading: enabled && !!groupId ? isLoading : false,
    error,
    busy,
    mutationError,
    refresh,
    contribute,
    withdraw,
  }
}
